export type PreferredLocale = 'PT_BR' | 'EN' | 'ES';

const LOCALE_BY_LNG: Record<string, PreferredLocale> = { 'pt-BR': 'PT_BR', en: 'EN', es: 'ES' };

export const toPreferredLocale = (lng: string | null | undefined): PreferredLocale =>
  (lng && LOCALE_BY_LNG[lng]) || 'PT_BR';

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const LAST_SENT_PREFIX = 'masp:preferred-locale:';
const DISABLED_KEY = 'masp:preferred-locale:disabled';

/**
 * Rota ainda não implantada: o gateway responde 404/405 sem `code`. Um 404 de negócio da API
 * (usuário não encontrado) traz `code` e não pode desligar a sincronização da aba inteira.
 */
const isRouteMissing = (error: unknown) => {
  const { status, code } = (error ?? {}) as { status?: number; code?: unknown };
  return (status === 404 || status === 405) && (code === undefined || code === null);
};

const safeGet = (storage: KeyValueStorage | null, key: string) => {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeSet = (storage: KeyValueStorage | null, key: string, value: string) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage bloqueado (aba anônima, cota): a deduplicação em memória continua valendo.
  }
};

/**
 * Sincroniza o idioma preferido do usuário logado com a API, sem nunca propagar erro para a UI.
 * Um envio por vez por usuário: trocas rápidas de idioma convergem para o último valor pedido,
 * em vez de a ordem de resposta de requisições concorrentes decidir. Deduplica pelo último valor
 * confirmado (memória + storage da sessão) e desliga na sessão quando a rota não existe.
 */
export function createPreferredLocaleSync(
  send: (locale: PreferredLocale) => Promise<unknown>,
  storage: KeyValueStorage | null
) {
  const confirmed = new Map<string, PreferredLocale>();
  const desired = new Map<string, PreferredLocale>();
  const running = new Map<string, Promise<void>>();
  let disabled = safeGet(storage, DISABLED_KEY) === '1';

  const confirmedFor = (userId: string) =>
    confirmed.get(userId) ?? (safeGet(storage, LAST_SENT_PREFIX + userId) as PreferredLocale | null);

  /** Devolve false quando parou por falha (não reagenda sozinho). */
  const drain = async (userId: string): Promise<boolean> => {
    for (;;) {
      const target = desired.get(userId);
      if (!target || disabled || confirmedFor(userId) === target) return true;
      try {
        await send(target);
        confirmed.set(userId, target);
        safeSet(storage, LAST_SENT_PREFIX + userId, target);
      } catch (error) {
        if (isRouteMissing(error)) {
          disabled = true;
          safeSet(storage, DISABLED_KEY, '1');
        }
        // Falha transitória: para aqui; a próxima troca de estado tenta de novo.
        if (desired.get(userId) === target) return false;
      }
    }
  };

  const start = (userId: string): Promise<void> => {
    const task = drain(userId).then((ok) => {
      running.delete(userId);
      // Pedido que chegou entre o fim do laço e esta limpeza ainda precisa ser enviado.
      if (ok && !disabled && desired.get(userId) !== confirmedFor(userId)) return start(userId);
    });
    running.set(userId, task);
    return task;
  };

  return function sync(userId: string | null | undefined, lng: string | null | undefined): Promise<void> {
    if (!userId || disabled) return Promise.resolve();
    desired.set(userId, toPreferredLocale(lng));
    return running.get(userId) ?? start(userId);
  };
}
